import frontViewPlaceholder from "~/assets/img/upload_placeholders/front_view.svg";
import fullFaceWithSmilePlaceholder from "~/assets/img/upload_placeholders/full_face_with_smile.svg";
import fullFaceWithoutSmilePlaceholder from "~/assets/img/upload_placeholders/full_face_without_smile.svg";
import lateralViewLeftPlaceholder from "~/assets/img/upload_placeholders/lateral_view_left.svg";
import lateralViewRightPlaceholder from "~/assets/img/upload_placeholders/lateral_view_right.svg";
import occlusalViewLowerPlaceholder from "~/assets/img/upload_placeholders/occlusal_view_lower.svg";
import occlusalViewUpperPlaceholder from "~/assets/img/upload_placeholders/occlusal_view_upper.svg";
import profilePlaceholder from "~/assets/img/upload_placeholders/profile.svg";
import type { TCorrectionMediaSemantics } from "~/types/patient";

/**
 * The empty-state artwork for the eight clinical photo views — the Figma card (`5237:104992`): a
 * bordered white card with a grey line illustration and a baked-in "Загрузите фото" caption, at the
 * SVG's own 314×172.
 *
 * Shared by the legacy uploader (`file_upload.tsx`) and the classifier's review grid, which draws the
 * same empty card so an unfilled view looks identical either way.
 *
 * Imports produce content-hashed URLs so changed artwork invalidates browser caches. Unlisted semantics,
 * including `recipe`, retain their legacy artwork without a baked frame or caption.
 */
export const REDESIGNED_PHOTO_PLACEHOLDER_SRC: Record<TCorrectionMediaSemantics, string> = {
  profile: profilePlaceholder,
  full_face_with_smile: fullFaceWithSmilePlaceholder,
  full_face_without_smile: fullFaceWithoutSmilePlaceholder,
  occlusal_view_upper: occlusalViewUpperPlaceholder,
  occlusal_view_lower: occlusalViewLowerPlaceholder,
  lateral_view_right: lateralViewRightPlaceholder,
  front_view: frontViewPlaceholder,
  lateral_view_left: lateralViewLeftPlaceholder,
};

/**
 * The baked card for a semantics, or `null` for one that has none — `recipe`, whose art carries no
 * frame or caption and keeps the legacy treatment. Callers branch on the null rather than testing
 * membership separately, so "is it redesigned?" and "what do I draw?" can never disagree.
 */
export function redesignedPhotoPlaceholder(semantics: string): string | null {
  // Takes a plain `string`, not the semantics union, because it is a MEMBERSHIP test as much as a
  // lookup: `recipe` is the documented miss, and callers reach it from loosely-typed legacy props
  // where an unlisted value is entirely possible. Anything absent gets the legacy treatment.
  const byName: Record<string, string> = REDESIGNED_PHOTO_PLACEHOLDER_SRC;
  return byName[semantics] ?? null;
}
